
import { append } from 'ramda';
import EDForm, {
  IState as IFormState,
} from '../../../../../../../../../../../BaseComponents/Forms/EDForm';
import {
  IFormIssues,
  IErrors,
  IFieldError,
  IFormBag,
  IWarnings,
  IFormActions,
} from '../../../../../../../../../../../Types/FormTypes';
import { ekanalys_ekanalys_columns } from '../../../../../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { editColumnFormValuesToColumn } from '../Utils/EkonomiskAnalysConvertEditColumnFormValuesToColumn';
import { columnToEditColumnFormValues } from '../Utils/EkonomiskAnalysConvertColumnToEditColumnFormValues';
import Logger from '../../../../../../../../../../../Utils/Logger';
import { StandardColumnType } from './Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import { getColumnDisplayLabelType } from './Queries/EkonomiskAnalysEditColumnCaptionQuery/EkonomiskAnalysEditColumnManualCaptionQuery';
import { IEkonomiskAnalysDates } from '../../../../../AnalysSettingsHandler/AnalysSettingsHandler';

export enum BudgetTypes {
  all = 'A',
  category = 'T',
  specific = 'B',
}

export type ColumnFaltkodType =
  | 'PERVARDE'
  | 'PERINGVARDE'
  | 'PERUTGVARDE'
  | 'PERBUDGET'
  | 'PERINGBUDGET'
  | 'PERUTGBUDGET'
  | 'DIFFPERVARDEBUDGET'
  | 'PROCENTPERVARDEBUDGET'
  | 'DIFFPERINGVARDEBUDGET'
  | 'PROCENTPERINGVARDEBUDGET'
  | 'DIFFPERUTGVARDEBUDGET'
  | 'PROCENTPERUTGVARDEBUDGET'
  | 'ARETSVARDE'
  | 'ARETSINGBALANS'
  | 'ARETSUTGBALANS'
  | 'ARETSBUDGET'
  | 'DIFFARETSVARDEBUDGET'
  | 'PROCENTARETSVARDEBUDGET';

export type EkonomiskAnalysEditColumnFormValuesType = {
  columnOrigin: ColumnFaltkodType | null;
  displayLabel: string;
  title: string;
  subTitle: string;
  columnType: number | null;
  columnUnit: number | null;
  timeInterval: number | null;
  fromAr: number | null;
  fromArChecked: boolean;
  fromPeriod: number | null;
  fromPeriodChecked: boolean;
  tomAr: number | null;
  tomArChecked: boolean;
  tomPeriod: number | null;
  tomPeriodChecked: boolean;
  budget: string | null;
  budgetType: BudgetTypes | null;
  flipSign: boolean;
  rullande: boolean | null;
};

interface IProps {
  column: ekanalys_ekanalys_columns;
  setColumn: (
    newColumn: ekanalys_ekanalys_columns,
    newName: string,
    newDisplayLabel: string
  ) => void;
  children: (input: IFormBag) => React.ReactNode;
  dates: IEkonomiskAnalysDates;
  standardColumns: StandardColumnType[];
  getColumnDisplayLabel: getColumnDisplayLabelType;
}

const getColumnName = (column: ekanalys_ekanalys_columns): string => {
  let name = column.CODE_CAPTION || 'Default Col caption';

  if (column.BENAMNING) {
    if (column.BENAMNING2) {
      name = `${column.BENAMNING} (${column.BENAMNING2})`;
    } else {
      name = column.BENAMNING;
    }
  }

  return name;
};

const addError = (
  fieldName: string,
  error: IFieldError,
  errors: IErrors
): IErrors => {
  const fieldErrors = append(error, errors[fieldName]);
  const newErrors: IErrors = { ...errors, [fieldName]: fieldErrors };
  return newErrors;
};

const editColumnFormValidate = (
  values: EkonomiskAnalysEditColumnFormValuesType,
  formState: IFormState
): IFormIssues => {
  let errors: IErrors = {};
  if (values.columnOrigin == null || (values.columnOrigin as string) === '') {
    errors = addError(
      'columnOrigin',
      { id: 'Empty', message: 'Måste ange värde' },
      errors
    );
  }

  if (values.budgetType === BudgetTypes.category && !values.budget) {
    errors = addError(
      'budget',
      { id: 'Empty', message: 'Måste välja kategori' },
      errors
    );
  }
  if (values.budgetType === BudgetTypes.specific && !values.budget) {
    errors = addError(
      'budget',
      { id: 'Empty', message: 'Måste välja specifik budget' },
      errors
    );
  }

  return { errors, warnings: {} };
};

const onSubmit =
  (
    inititalColumn: ekanalys_ekanalys_columns,
    setColumn: (
      newColumn: ekanalys_ekanalys_columns,
      newName: string,
      newDisplayLabel: string
    ) => void,
    standardColumns: StandardColumnType[],
    getColumnDisplayLabel: getColumnDisplayLabelType,
    referenceDates: IEkonomiskAnalysDates
  ) =>
  (
    values: EkonomiskAnalysEditColumnFormValuesType,
    warnings: IWarnings,
    formActions: IFormActions
  ) => {
    let column: ekanalys_ekanalys_columns | undefined;
    try {
      column = editColumnFormValuesToColumn(
        values,
        inititalColumn,
        standardColumns,
        referenceDates
      );

      getColumnDisplayLabel(column)
        .then(displayLabel => {
          if (column) {
            setColumn(column, getColumnName(column), displayLabel);
          }
        })
        .catch(error => {
          Logger.error(error instanceof Error ? error : String(error));
        });
    } catch (error) {
      Logger.error(error instanceof Error ? error : String(error));
    }
  };

const EkonomiskAnalysEditColumnForm: React.FunctionComponent<
  IProps
> = props => {
  let initialFormValues: EkonomiskAnalysEditColumnFormValuesType;

  initialFormValues = columnToEditColumnFormValues(
    props.column,
    props.standardColumns,
    props.dates
  );

  return (
    <EDForm
      initialValues={initialFormValues}
      validate={editColumnFormValidate as unknown as (values: Record<string, unknown>, formState: IFormState) => IFormIssues}
      onSubmit={onSubmit(
        props.column,
        props.setColumn,
        props.standardColumns,
        props.getColumnDisplayLabel,
        props.dates
      ) as unknown as (
        values: Record<string, unknown>,
        warnings: IWarnings,
        formActions: IFormActions
      ) => void}
      enableReinitialize
    >
      {props.children}
    </EDForm>
  );
};

export default EkonomiskAnalysEditColumnForm;
